import { ChevronsUpDown } from 'lucide-react'
import * as React from 'react'

import { Button } from '../../primitives/Button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface CheckboxFilterOption {
  id: string
  label: string
  value: string | boolean
}

interface CheckboxFilterProps {
  /**
   * Filter title
   */
  title: string
  /**
   * Filter options
   */
  options: CheckboxFilterOption[]
  /**
   * Selected values (option ids)
   */
  value: string[]
  /**
   * Callback when values change
   */
  onChange: (_values: string[]) => void
  /**
   * Button icon
   */
  icon?: React.ReactNode
  /**
   * If true, only one option can be selected (radio behavior)
   */
  exclusive?: boolean
  /**
   * ID of the option that represents "all"
   */
  allOptionId?: string
  /**
   * Popover size variant
   * - 'default': 229px width (states)
   * - 'small': 157px width (returned, yes/no)
   */
  variant?: 'default' | 'small'
  /**
   * Custom width for the popover (e.g. '132px')
   */
  popoverWidth?: string
  /**
   * Custom max-height for the options list (e.g. '144px')
   */
  itemsMaxHeight?: string
  /**
   * Popover alignment relative to trigger button
   * @default 'end' - maintains current behavior
   */
  popoverAlign?: 'start' | 'center' | 'end'
}

/**
 * CheckboxFilter - Checkbox filter in popover
 * Reusable component for multiple selection or exclusive filters
 */
export function CheckboxFilter({
  title,
  options,
  value,
  onChange,
  icon,
  exclusive = false,
  allOptionId = 'all',
  variant = 'default',
  popoverWidth,
  itemsMaxHeight,
  popoverAlign = 'end',
}: CheckboxFilterProps) {
  const isSmall = variant === 'small'
  const containerWidth = popoverWidth ?? (isSmall ? '157px' : '229px')
  const maxHeight = itemsMaxHeight ?? (isSmall ? '112px' : 'min(400px, 60vh)')
  const handleCheckboxChange = (optionId: string, checked: boolean) => {
    if (exclusive) {
      // Exclusive behavior: only one option selected at a time
      if (checked) {
        onChange([optionId])
      } else {
        // If unchecked, return to "all"
        onChange([allOptionId])
      }
      return
    }

    // Normal multiple checkbox behavior
    if (optionId === allOptionId) {
      // If selecting "all", activate only 'all'
      if (checked) {
        onChange([allOptionId])
      }
      return
    }

    // For other options
    if (checked) {
      // Add new option and remove "all" if it was there
      const newValues = value.filter((v) => v !== allOptionId)
      onChange([...newValues, optionId])
    } else {
      // Remove option
      const newValues = value.filter((v) => v !== optionId)
      // If no options left, return to "all"
      onChange(newValues.length === 0 ? [allOptionId] : newValues)
    }
  }

  // Determine button label
  const getButtonLabel = (): string => {
    if (value.includes(allOptionId) || value.length === 0) {
      return title
    }

    if (value.length === 1) {
      const selectedOption = options.find((opt) => opt.id === value[0])
      return selectedOption?.label || title
    }

    return `${title} (${value.length})`
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'h-9 min-w-fit shrink-0 justify-start px-3 font-normal text-[#262626]',
            'border-[#D4D4D4] bg-[#FAFAFA] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
            'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]'
          )}
          icon={icon}
          iconPosition="left"
          size="sm"
          variant="outline"
        >
          <span className="flex items-center gap-2">
            {getButtonLabel()}
            <ChevronsUpDown className="h-4 w-4" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={popoverAlign}
        className="p-0"
        side="bottom"
        sideOffset={4}
        style={{
          boxSizing: 'border-box',
          width: containerWidth,
          background: '#FAFAFA',
          border: '1px solid #D4D4D4',
          boxShadow:
            '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1)',
          borderRadius: '6px',
        }}
      >
        {/* Filtros/Contenedores/Estados o Retornado */}
        <div
          className="flex flex-col items-start"
          style={{ padding: '0px', gap: '8px' }}
        >
          {/* Items */}
          <div
            className="flex w-full touch-pan-y flex-col items-start overflow-y-auto"
            onTouchMove={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onWheel={(e) => {
              e.stopPropagation()
            }}
            style={{
              padding: '8px',
              maxHeight: maxHeight,
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            {options.map((option) => {
              const isChecked = value.includes(option.id)
              return (
                /* Menu Item */
                <div
                  className="flex w-full cursor-pointer items-center hover:bg-white/50"
                  key={option.id}
                  onClick={(e) => {
                    // Ignore clicks that originate from the actual input or its label
                    const target = e.target as HTMLElement
                    const tag = target.tagName.toLowerCase()
                    if (tag === 'input' || tag === 'label') return

                    // Also ignore clicks on svg paths or icons inside the checkbox
                    if (target.closest && target.closest('input')) return

                    handleCheckboxChange(option.id, !isChecked)
                  }}
                  style={{
                    padding: '4px 8px',
                    gap: '8px',
                    height: '32px',
                    minHeight: '32px',
                    background: '#FAFAFA',
                    borderRadius: '6px',
                  }}
                >
                  {/* AL - Content container */}
                  <div
                    className="flex items-center"
                    style={{ gap: '8px', height: '20px' }}
                  >
                    {/* Decoration container 20x20 */}
                    <div
                      className="flex items-center justify-center"
                      style={{ width: '20px', height: '20px' }}
                    >
                      <Checkbox
                        checked={isChecked}
                        className={cn(
                          'h-4 w-4',
                          'data-[state=checked]:border-[#1E293B]',
                          'data-[state=checked]:bg-[#1E293B]',
                          'data-[state=checked]:text-[#FAFAFA]'
                        )}
                        id={option.id}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(option.id, !!checked)
                        }
                      />
                    </div>
                    {/* Label container */}
                    <div
                      className="flex flex-col items-start justify-center"
                      style={{ padding: '0px', height: '20px', flexGrow: 1 }}
                    >
                      <label
                        className="cursor-pointer text-[14px] font-normal leading-5 tracking-[0.005em]"
                        htmlFor={option.id}
                        style={{ color: '#262626', height: '20px' }}
                      >
                        {option.label}
                      </label>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
